import JSON5 from "json5";

import type BaseModel from "../../models/BaseModel";
import type BaseObject from "../../models/BaseObject";
import { ChannelType } from "../../models/ChannelType";
import type Consumer from "../../models/Consumer";
import { noItx } from "../../translationItx/itx";

import { EstadaFunctionResult } from "../EstadaFunctionResult";
import { EstadaFunctionContext } from "../EstadaFunctionContext";
import { EnumFunctionTypes, EnumLightTypes } from "../DefGPTParameter";

import Logger from "../../Logger";
import Device from "../../models/Device";
import { PageTypes } from "../../models/PageTypes";
import { DeviceClasses } from "../../models/DeviceClasses";
const logger = new Logger();

export class FunctionHelpers {
  static validateParameters(params: any, required: string[]): string | null {
    for (const field of required) {
      if (!params[field] || (typeof params[field] === "string" && params[field].trim() === "")) {
        return `Required parameter '${field}' is missing or empty`;
      }
    }
    return null;
  }

  static sanitizeInput(input: string): string {
    if (!input) return "";
    return input.trim().replace(/[<>]/g, ""); // Entferne potentiell gefährliche Zeichen
  }

  static stripTypeFragments(name: string): string {
    name = name.replace("dimmbare", ""); // no itx
    name = name.replace("schaltbare", ""); // no itx

    return name.trim();
  }

  static matchStrings(s1?: string, s2?: string): boolean {
    if (s1 && s2) {
      return s1.toLocaleLowerCase() == s2.toLocaleLowerCase();
    } else {
      return false;
    }
  }

  static matchTagOrName(o?: BaseObject, s2?: string): boolean {
    if (!s2) {
      return true;
    } else {
      if (o && s2) {
        if (FunctionHelpers.matchStrings(o.name, s2)) {
          return true;
        }
        if (FunctionHelpers.matchStrings(o.tag, s2)) {
          return true;
        }
        if (FunctionHelpers.matchStrings(o.simpleFullTag, s2)) {
          return true;
        }
      }
      return false;
    }
  }

  static matchObjects(params: {
    name?: string;
    fullTag?: string;
    type?: string;
    parent?: string;
    parent2?: string;
    id?: string;
    className?: string;
  }): Array<BaseModel> {
    const { name, fullTag, type, parent, parent2, id, className } = params;

    if (id) {
      const obj = state.objectRegistry.getObject(id) as BaseModel;
      if (obj) {
        return [obj];
      }
    }

    if (name && !fullTag && !type && !parent && !parent2) {
      return [];
    }

    let matchedObjects = new Array<BaseModel>();

    if (fullTag) {
      state.objectRegistry.allObjects.forEach((o) => {
        if ((!className || o.className === className) && o.simpleFullTag == fullTag) {
          matchedObjects.push(o as BaseModel);
        }
      });
    } else {
      state.objectRegistry.allObjects.forEach((o) => {
        if ((!className || o.className === className) && FunctionHelpers.matchTagOrName(o, name)) {
          matchedObjects.push(o as BaseModel);
        }
      });
    }

    if (matchedObjects.length <= 1) {
      return matchedObjects;
    }

    let matched = new Array<BaseModel>();

    if (parent) {
      matchedObjects.forEach((o) => {
        if (
          FunctionHelpers.matchTagOrName(o.parent, parent) &&
          FunctionHelpers.matchTagOrName(o.parent?.parent, parent2)
        )
          matched.push(o);
      });
    } else {
      matched = matchedObjects;
    }

    return matched;
  }

  static simplyfiedItem(obj: BaseModel): any {
    const res: any = {
      class: obj.className,
      id: obj.id,
      fullTag: obj.simpleFullTag,
      name: obj.name,
    };

    if (obj.isDevice) {
      const knxProductData = (obj as Device).template?.knxProductData;
      if (knxProductData) {
        res.orderID = knxProductData.productInfo.OrderNumber;
        // res.EAN = knxProductData.EAN;
      } else {
        res.orderID = "no product assigned yet";
      }
    }

    return res;
  }

  static resultItemWithNavigation(obj: BaseModel, action: string): any {
    let floorName: string | undefined = undefined;
    let roomName: string | undefined = undefined;

    switch (obj.className) {
      case noItx("Room"): {
        floorName = obj.parent?.name;
        return {
          action: action,
          class: "room", // no itx
          id: obj.id,
          fullTag: obj.simpleFullTag,
          name: obj.name,
          floorName: floorName,
          url: `http://estada.de/navigate/?id=${obj.id}`,
        };
      }

      case noItx("Actor"): {
        const cabinetName = obj.parent?.name;
        return {
          action: action,
          class: "actor", // no itx
          id: obj.id,
          fullTag: obj.simpleFullTag,
          name: obj.name,
          cabinetName: cabinetName,
          url: `http://estada.de/navigate/?id=${obj.id}`,
        };
      }

      case noItx("Button"): {
        floorName = obj.parent?.parent?.name;
        roomName = obj.parent?.name;
        return {
          action: action,
          class: "Panel", // no itx
          id: obj.id,
          fullTag: obj.simpleFullTag,
          name: obj.name,
          floorName: floorName,
          roomName: roomName,
          url: `http://estada.de/navigate/?id=${obj.id}`,
        };
      }

      case noItx("Consumer"): {
        let t = "";
        floorName = obj.parent?.parent?.name;
        roomName = obj.parent?.name;

        const consumer = obj as Consumer;
        if (consumer.channelType == ChannelType.Heating) {
          t = "function:heating"; // no itx
        } else if (consumer.channelType == ChannelType.Blind) {
          t = "function:blind"; // no itx
        } else if (consumer.channelType == ChannelType.Jalousie) {
          t = "function:jalousie"; // no itx
        } else {
          t = "light"; // no itx
        }

        return {
          action: action,
          class: t,
          id: obj.id,
          fullTag: obj.simpleFullTag,
          name: obj.name,
          floorName: floorName,
          roomName: roomName,
          url: `http://estada.de/navigate/?id=${obj.id}`,
        };
      }

      default: {
        return {
          action: action,
          id: obj.id,
          fullTag: obj.simpleFullTag,
          name: obj.name,
          url: `http://estada.de/navigate/?id=${obj.id}`,
        };
      }
    }
  }

  static stripParentNameFragments(name: string, parentName?: string): string {
    if (name && parentName) {
      const nameL = name.toLocaleLowerCase();
      const parentNameL = parentName.toLocaleLowerCase();

      const i = nameL.indexOf(" " + parentNameL);
      if (i > 0) {
        name = name.substring(0, i) + name.substring(i + parentNameL.length + 1, name.length);
      }
    }

    return name.trim();
  }

  static decodeUnicode(x: string): string {
    var r = /\\u([\d\w]{4})/gi;
    x = x.replace(r, function (match, grp) {
      return String.fromCharCode(parseInt(grp, 16));
    });

    ///// Unicode Hex Character Code i.e. "&#xFC;"
    r = /\&\#x([\d\w]{2});/gi;
    x = x.replace(r, function (match, grp) {
      return String.fromCharCode(parseInt(grp, 16));
    });

    return x;
  }

  static getChannelTypeFromString(type: EnumLightTypes | EnumFunctionTypes): {
    channelType: ChannelType;
    isFunction: boolean;
  } {
    let channelType = ChannelType.Switch;
    let isFunction = true;

    switch (type) {
      case EnumLightTypes.dimmable:
        channelType = ChannelType.Dimmer;
        break;

      case EnumLightTypes.switchable:
        channelType = ChannelType.Switch;
        break;

      case EnumLightTypes.tunableWhite:
        channelType = ChannelType.TuneableWhite;
        break;

      case EnumLightTypes.rgb:
        channelType = ChannelType.Rgb;
        break;

      case EnumFunctionTypes.heating:
        channelType = ChannelType.Heating;
        isFunction = true;
        break;

      case EnumFunctionTypes.jalousie:
        channelType = ChannelType.Jalousie;
        isFunction = true;
        break;

      case EnumFunctionTypes.blind:
        channelType = ChannelType.Blind;
        isFunction = true;
        break;

      case EnumFunctionTypes.sockets:
        channelType = ChannelType.plug_230V;
        isFunction = true;
        break;

      case EnumFunctionTypes.switchableSockets:
        channelType = ChannelType.plug_Switch;
        isFunction = true;
        break;

      case EnumFunctionTypes.windowContact:
        channelType = ChannelType.Lock;
        isFunction = true;
        break;

      default:
        logger.log(`ChannelType of light or function unkwon: ${type}`);
        channelType = ChannelType.Switch;
    }

    return { channelType: channelType, isFunction: isFunction };
  }

  static getGermanNameOfListIfTranslationNotWorking(value: PageTypes): string {
    let res = "";
    switch (value) {
      case PageTypes.addressList: {
        res = noItx("Adress-Liste");
        break;
      }
      case PageTypes.scenelist: {
        res = noItx("Szenen-Liste");
        break;
      }
      case PageTypes.multiplexerlist: {
        res = noItx("Multiplexer-Liste");
        break;
      }
      // case PageTypes.cabletypelist: {
      //   res = noItx("Kabel-Liste");
      //   break;
      // }

      case PageTypes.cableroutingList: {
        res = noItx("Kabelaufzugliste");
        break;
      }
      case PageTypes.cablepullList: {
        res = noItx("Kabelzuglist");
        break;
      }
      case PageTypes.cabinetPartList: {
        res = noItx("Schaltschrank Stückliste");
        break;
      }
      case PageTypes.partsList: {
        res = noItx("Stücklisten");
        break;
      }
      case PageTypes.pricePartsList: {
        res = noItx("Preisliste");
        break;
      }
    }
    return res;
  }

  static nameAndLink(obj: BaseModel): string {
    return `[${obj.name}](/http://estada.de/navigate/?id=${obj.id})`;
  }

  static objsToMarkdown(objs: Array<BaseModel>): string {
    let content = "";
    objs.forEach((obj) => {
      content += "* "; // no itx
      content += FunctionHelpers.nameAndLink(obj);
      content += "\n"; // no itx
    });
    return content;
  }

  static renumber(sortedObjs: Array<BaseModel>, prefix: string, startIndex: number) {
    if (sortedObjs.length == 0) {
      return;
    }

    let i = 1;
    if (startIndex) {
      i = startIndex;
    }

    sortedObjs.forEach((obj) => {
      let calcTag = obj.tag;
      let size = obj.tagSize;

      calcTag = prefix + `${i}`;
      i++;
      if (size == 2) {
        calcTag = calcTag + "," + prefix + `${i}`;
        i++;
      } else if (size != 1) {
        calcTag = calcTag + "-" + prefix + `${i + size - 2}`;
        i = i + size - 1;
      }
      obj.tag = calcTag;
      obj.raiseOnChange("tag");
    });
  }

  static mergeEstadaFunctionResults(
    res1: EstadaFunctionResult | undefined,
    res2: EstadaFunctionResult | undefined,
  ): EstadaFunctionResult {
    if (!res1) {
      return res2 || { createdObjects: [], usedObjects: [] };
    }

    if (!res2) {
      return res1;
    }

    const res: EstadaFunctionResult = {
      createdObjects: [...(res1.createdObjects || []), ...(res2.createdObjects || [])],
      usedObjects: [...(res1.usedObjects || []), ...(res2.usedObjects || [])],
      queriedObjects: [...(res1.queriedObjects || []), ...(res2.queriedObjects || [])],
    };

    if (res1.error && res2.error) {
      res.error = res1.error + "\n" + res2.error;
    } else {
      res.error = res1.error || res2.error;
    }

    if (res1.userFeedback && res2.userFeedback) {
      res.userFeedback = res1.userFeedback + "\n" + res2.userFeedback;
    } else {
      res.userFeedback = res1.userFeedback || res2.userFeedback;
    }

    if (res1.systemFeedback && res2.systemFeedback) {
      res.systemFeedback = res1.systemFeedback + "\n" + res2.systemFeedback;
    } else {
      res.systemFeedback = res1.systemFeedback || res2.systemFeedback;
    }

    // was macht man damit ??

    //          waitForAnAnswer: res1.waitForAnAnswer || res2.waitForAnAnswer || false,
    //            hideResponseFromUser: res1.hideResponseFromUser || res2.hideResponseFromUser || false,

    return res;
  }

  static getUniqueObject(
    context: EstadaFunctionContext,
    params: { name: string; type?: string; parent?: string; id?: string },
  ): { result?: EstadaFunctionResult; object?: BaseModel | undefined } {
    const { name, type, parent, id } = params;

    let className: string | undefined = undefined;

    let obj: BaseModel | undefined = undefined;

    if (id) {
      obj = state.objectRegistry.getObject(id);
    }

    let matched = new Array<BaseModel>();

    if (!obj) {
      matched = FunctionHelpers.matchObjects({ ...params, className });
      obj = matched.pop() as BaseModel;
    }

    if (obj) {
      let s = "";

      if (matched.length > 0) {
        s = `\n ${state.translate(
          "deleteOject.selectionNotClear",
          "Allerdings war die Auswahl nicht eindeutig.",
        )} \n ${state.translate(
          "deleteOject.followingSelection",
          "Es hätte folgende alternative Möglichkeiten gegeben:",
        )} \n`;
        matched.forEach((o) => (s += `${o.fullTag} ${o.name}`));
        return {
          result: {
            userFeedback: s,
          },
        };
      }
      return { object: obj };
    } else {
      return {
        result: {
          error: state.translate("deleteOject.objNotFound", `Das Objekt T1 wurde nicht gefunden`, {
            T1: name,
          }),
        },
      };
    }
  }

  static resultFromGtpToResultSet(content: string): {
    messageText: string;
    json: any | undefined;
    baseModels: Array<BaseModel> | undefined;
  } {
    function jsonToBasemodels(json: any): Array<BaseModel> | undefined {
      let res = new Array<BaseModel>();
      if (json && Array.isArray(json)) {
        json.forEach((item) => {
          if (item.id) {
            const obj = state.objectRegistry.getObject(item.id);
            if (obj) {
              res.push(obj as BaseModel);
            }
          } else {
            res = res.concat(jsonToBasemodels(item) || []);
          }
        });
      } else if (json && typeof json == "object") {
        for (let i in json) {
          res = res.concat(jsonToBasemodels(json[i]) || []);
        }
      }

      if (res.length == 0) {
        return undefined;
      } else {
        return res;
      }
    }

    let messageText = content;

    try {
      let jsonText = content;

      const markDownJsonStart = jsonText.indexOf("```json");
      if (markDownJsonStart < 0) {
        return { messageText: content, json: undefined, baseModels: undefined };
      }

      const markDownJsonEnd = jsonText.indexOf("```", markDownJsonStart + 4);
      if (markDownJsonEnd < 0) {
        return { messageText: content, json: undefined, baseModels: undefined };
      }

      jsonText = jsonText.substring(markDownJsonStart + 7, markDownJsonEnd);

      const json = JSON5.parse(jsonText);
      if (json?.messageText) {
        //
        // ChatGPT delivers the messageText in the JSON
        //

        messageText = json.messageText;
        delete json.messageText; // remove messageText from json
      } else {
        //
        // ChatGPT DOES NOT deliver the messageText in the JSON
        // We cut the into Text before the JSON as messageText
        //

        messageText = content.substring(0, markDownJsonStart);

        let indexJSON = messageText.toLocaleLowerCase().indexOf("json");
        if (indexJSON >= 0) {
          messageText = messageText.substring(0, indexJSON);
        }

        if (messageText.endsWith(":")) {
          messageText = messageText.substring(0, messageText.length - 1);
        }

        if (messageText.endsWith(" im")) {
          // cut "im " von "im JSON-Format"
          messageText = messageText.substring(0, messageText.length - 3);
        }

        messageText += "\n";
      }

      const baseModels = jsonToBasemodels(json);

      return { messageText, json, baseModels };
    } catch (ex) {
      logger.error("Error parsing result from GPT", ex);
    }

    return { messageText: content, json: undefined, baseModels: undefined };
  }

  static cleanUpTemplateGroups() {
    // after loading check if templateGroups are not to much
    let firstUnknownFound = false;
    state.templateGroups.forEach((tg) => {
      if (tg.templateGroupType == DeviceClasses.Unknown) {
        if (firstUnknownFound) {
          //// delete templateGroups who are to times inside
          tg.delete();
        } else {
          firstUnknownFound = true;
        }
      }
    });
  }
}
